import Icon from "assets/icons";
import classNames from "classnames";
import isArray from "lodash/isArray";
import React from "react";
import { FilterProps, FilterType } from "react-table";
import SearchWrapper from "shared/components/searchWrapper";
import { FormFieldOption } from "shared/form/fields/types";
import { useCopies } from "shared/hooks/useCopies";
import useOnOutSideClick from "shared/hooks/useOnOutSideClick";
import DateTimeHelper from "shared/utils/dateTimeHelper";

export function DefaultColumnFilter<T extends object>(props: FilterProps<T>) {
  return (
    <React.Fragment>
      <SearchWrapper
        appliedSearchText={props.column.filterValue || ""}
        searchChangeHandler={e => props.column.setFilter(e || undefined)}
        compactStyle
        hidePlaceholder
      />
    </React.Fragment>
  );
}

export function DefaultColumnFilters<T extends object>(props: FilterProps<T>) {
  const copies = useCopies();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const tableColumnSearchRef = React.useRef<HTMLDivElement>(null);
  const [showSearch, setShowSearch] = React.useState<Boolean>(false);
  const [showFilterIcon, setShowFilterIcon] = React.useState<Boolean>(false);

  React.useEffect(() => {
    if (showSearch) inputRef.current?.focus();
  }, [showSearch]);

  const onClose = React.useCallback(() => {
    setShowSearch(false);
  }, []);

  const clearSearch = React.useCallback(() => {
    if (inputRef.current) {
      setShowFilterIcon(false);
      inputRef.current?.focus();
      inputRef.current.value = "";
      props.column.setFilter(undefined); // Set undefined to remove the filter entirely
    }
  }, [props.column]);

  const onFilterSearch = React.useCallback(() => {
    if (inputRef.current?.value !== "") {
      setShowFilterIcon(true);
    } else {
      setShowFilterIcon(false);
    }
    props.column.setFilter(inputRef.current?.value);
  }, [props.column]);

  useOnOutSideClick({ node: tableColumnSearchRef?.current, onOutSideClick: onClose });

  return (
    <div className="thead_column_filter" ref={tableColumnSearchRef}>
      <span onClick={() => setShowSearch(c => !c)} className={classNames("", { show: showSearch || showFilterIcon })}>
        {showFilterIcon ? <Icon.Filter /> : <Icon.Search />}
      </span>
      <div className={classNames("thead_column_filter_input", { show: showSearch })}>
        <input
          ref={inputRef}
          onChange={onFilterSearch}
          placeholder={copies.get("search")}
          value={props.column.filterValue || ""}
        />
        <span
          onClick={clearSearch}
          title={copies.get("clear")}
          className={classNames("thead_column_filter_clear", {
            show: inputRef.current?.value !== ""
          })}>
          <Icon.Close title={copies.get("clear")} />
        </span>
      </div>
    </div>
  );
}

export function SelectColumnFilter<T extends object>(props: FilterProps<T> & { options?: Array<FormFieldOption> }) {
  const copies = useCopies();
  const id = props.column.id;
  const preFilteredRows = props.preFilteredRows;

  const options = React.useMemo(() => {
    const options = new Set<string>();

    function AddOptionsFromArray(arr: Array<any>) {
      arr.forEach(val => {
        options.add(val);
      });
    }
    function AddOption(val: any) {
      if (val) {
        options.add(val);
      }
    }

    preFilteredRows.forEach(row => {
      if (isArray(row.values[id])) {
        AddOptionsFromArray(row.values[id]);
      } else {
        AddOption(row.values[id]);
      }
    });

    if (props.options) {
      return [
        {
          label: `${copies.get("all")} `,
          value: ""
        },
        ...props.options
      ];
    }

    return [
      {
        label: `${copies.get("all")} `,
        value: ""
      },
      ...Array.from(options)
        .sort()
        .map(option => {
          return {
            label: option,
            value: option
          };
        })
    ];
  }, [id, preFilteredRows, copies, props.options]);

  return (
    <div className="thead_column_filter_select">
      <select
        value={props.column.filterValue}
        onChange={e => {
          props.column.setFilter(e.target.value || undefined);
          props.column.filter = "equals";
        }}>
        {options.map((option, i) => (
          <option key={i} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export const dateFilterFunction: FilterType<any> = (rows, id, filterValue) => {
  return rows.filter(row => {
    const columnId = id[0];
    const rowValue = row.values[columnId];
    const formattedDate = rowValue
      ? DateTimeHelper.formatDate({
          startDate: rowValue,
          option: { showWeekDay: true, showYear: true, showTime: true }
        })
      : "";
    return formattedDate.toLowerCase().includes(filterValue.toLowerCase());
  });
};
